import MeatballMenuButton from 'components/atoms/meatballMenuButton';
import PillButton from 'components/atoms/pillButton';
import RectangleImageButton from 'components/atoms/rectangleImageButton';
import {useTheme} from 'contexts/ThemeContext';
import {
  determineSectionItemType,
  SectionType,
} from 'helpers/determineSectionItemType';
import React from 'react';
import {SectionList, SectionListRenderItem, Text, View} from 'react-native';
import {SectionListFormat} from 'stores/ClassStore';
import {StudentScheduleFormat} from 'stores/ClassStore';
import strings from 'translations';
import DeleteSchedule from '../deleteSchedule';
import styleSheet from './styles';

interface LessonListProps {
  schedule: SectionListFormat[];
  showCreateClass: boolean;
  onPressCreateClass: () => void;
  onPressJoinToClass: (id: string) => void;
  renderItem?: SectionListRenderItem<StudentScheduleFormat, SectionListFormat>;
  onPressCancelButton(id: string): void;
  onPressDeleteButton(id: string): void;
}

interface ItemProps {
  item: StudentScheduleFormat;
  type: number;
  isCurrent: boolean;
}

const LessonList = (props: LessonListProps) => {
  const theme = useTheme();
  const styles = styleSheet(theme);
  const {schedule} = props;

  const PillButtonContainer = (
    <View style={styles.pillButtonContainer}>
      {props.showCreateClass && (
        <PillButton
          onPress={props.onPressCreateClass}
          image={theme.images.addIcon}
          title={strings.homeScreen.create}
          textStyle={styles.createButtonTextStyle}
        />
      )}
    </View>
  );

  const Item = ({item, type, isCurrent}: ItemProps) => {
    let shadowStyle = {};
    let containerStyle = {};
    let contentStyle = {};

    switch (type) {
      case SectionType.firstItem:
        shadowStyle = styles.shadowOverflowFirstItem;
        containerStyle = styles.firstSectionItemWrapper;
        contentStyle = styles.sectionItem;
        break;

      case SectionType.lastItem:
        shadowStyle = styles.shadowOverflowLastItem;
        containerStyle = styles.lastSectionItemWrapper;
        contentStyle = styles.lastSectionItem;
        break;

      case SectionType.regularItem:
        shadowStyle = styles.shadowOverflowRegularItem;
        containerStyle = styles.sectionItemWrapper;
        contentStyle = styles.sectionItem;
        break;

      case SectionType.singleItem:
        shadowStyle = styles.shadowOverflowSingleItem;
        containerStyle = styles.oneSectionItemWrapper;
        contentStyle = styles.lastSectionItem;
        break;
    }

    return (
      <View style={shadowStyle}>
        <View style={containerStyle}>
          <View style={contentStyle}>
            <View>
              <Text style={styles.lessonTextStyle}>{item.title}</Text>
              <Text style={styles.textStyle}>{item.classMarking}</Text>
              <Text
                style={[
                  styles.textStyle,
                  styles.fontSize15,
                ]}>{`${item.scheduledTime}-${item.classEndTime}`}</Text>
            </View>
            <View>
              {isCurrent ? (
                <RectangleImageButton
                  onPress={() => props.onPressJoinToClass(item.sessionId)}
                  image={0}
                  backgroundColor={theme.colors.cornflowerBlue}
                  title={strings.homeScreen.joinLesson}
                  textStyle={styles.buttonTextStyle}
                />
              ) : props.showCreateClass ? (
                <DeleteSchedule
                  onPressCancelButton={() =>
                    props.onPressCancelButton(item.sessionId)
                  }
                  onPressDeleteButton={() =>
                    props.onPressDeleteButton(item.scheduleId)
                  }
                  title={item.title}
                  classMarking={item.classMarking}
                  scheduledTime={item.scheduledTime}
                  classEndTime={item.classEndTime}
                />
              ) : null}
            </View>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.sectionListView}>
      {schedule.length > 0 && (
        <SectionList
          style={styles.sectionList}
          sections={schedule}
          keyExtractor={(item, index) => item.title + index}
          stickySectionHeadersEnabled={false}
          renderItem={({item, index, section}) => {
            return (
              <Item
                item={item}
                type={determineSectionItemType(index, section, schedule)}
                isCurrent={section.title == strings.homeScreen.current}
              />
            );
          }}
          renderSectionHeader={({section: {title}}) => (
            <Text style={styles.sectionHeader}>{title}</Text>
          )}
          ListHeaderComponent={
            <View>
              <Text style={styles.listTitleText}>
                {strings.homeScreen.allLessons}
              </Text>
            </View>
          }
          renderSectionFooter={({section}) => {
            const index = schedule.indexOf(section);

            if (schedule.length == 0) {
              return PillButtonContainer;
            }

            return index == schedule.length - 1 ? (
              PillButtonContainer
            ) : (
              <View style={styles.sectionFooter}></View>
            );
          }}
        />
      )}
      {schedule.length == 0 && PillButtonContainer}
    </View>
  );
};

export default LessonList;
